import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import VideoEmbed from './VideoEmbed'
import './CommitCard.css'

const STATUS_LABEL = {
  verified: 'Verified project',
  submitted: 'In review',
  needs_evidence: 'Needs evidence',
  disputed: 'Disputed',
}

export default function CommitCard({ commit, onChange }) {
  const { user, profile } = useAuth()
  const profileData = commit.profiles
  const project = commit.projects
  const [liked, setLiked] = useState(!!commit.liked_by_me)
  const [likeCount, setLikeCount] = useState(commit.like_count || 0)
  const [flagged, setFlagged] = useState(!!commit.flagged_by_me)
  const [showFlagBox, setShowFlagBox] = useState(false)
  const [flagReason, setFlagReason] = useState('')
  const [showRemoveBox, setShowRemoveBox] = useState(false)
  const [removeReason, setRemoveReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [commentCount, setCommentCount] = useState(commit.comment_count || 0)
  const [showComments, setShowComments] = useState(false)
  const [comments, setComments] = useState(null) // null = not loaded yet
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [postingComment, setPostingComment] = useState(false)
  const [replyingTo, setReplyingTo] = useState(null) // comment id currently being replied to
  const [replyText, setReplyText] = useState('')
  const [postingReply, setPostingReply] = useState(false)

  const isAdmin = !!profile?.is_admin
  const name = profileData?.display_name || profileData?.full_name || 'Anonymous'
  const disputed = commit.dispute_status === 'disputed'
  const statusKey = disputed ? 'disputed' : project?.status
  const showStatusBadge = statusKey && STATUS_LABEL[statusKey]

  async function toggleLike() {
    if (!user) return window.location.assign('/login')
    if (busy) return
    setBusy(true)
    if (liked) {
      await supabase.from('commit_likes').delete().eq('commit_id', commit.id).eq('user_id', user.id)
      setLiked(false)
      setLikeCount(c => Math.max(0, c - 1))
    } else {
      await supabase.from('commit_likes').insert({ commit_id: commit.id, user_id: user.id })
      setLiked(true)
      setLikeCount(c => c + 1)
    }
    setBusy(false)
    onChange?.()
  }

  async function submitFlag() {
    if (!user) return window.location.assign('/login')
    if (busy || flagged) return
    setBusy(true)
    const { error } = await supabase
      .from('commit_flags')
      .insert({ commit_id: commit.id, user_id: user.id, reason: flagReason || null })
    if (!error) {
      setFlagged(true)
      setShowFlagBox(false)
    }
    setBusy(false)
    onChange?.()
  }

  async function submitRemove() {
    if (busy) return
    setBusy(true)
    const { error } = await supabase
      .from('commits')
      .update({ is_removed: true, removed_reason: removeReason || null, removed_by: user.id, removed_at: new Date().toISOString() })
      .eq('id', commit.id)
    setBusy(false)
    if (error) { alert(error.message); return }
    setShowRemoveBox(false)
    onChange?.()
  }

  async function restoreCommit() {
    if (busy) return
    setBusy(true)
    const { error } = await supabase
      .from('commits')
      .update({ is_removed: false, removed_reason: null, removed_by: null, removed_at: null })
      .eq('id', commit.id)
    setBusy(false)
    if (error) { alert(error.message); return }
    onChange?.()
  }

  async function toggleComments() {
    const opening = !showComments
    setShowComments(opening)
    if (opening && comments === null) {
      setCommentsLoading(true)
      const { data } = await supabase
        .from('commit_comments')
        .select('*, profiles!commit_comments_user_id_fkey(display_name, full_name, avatar_url, username), comment_likes(count)')
        .eq('commit_id', commit.id)
        .order('created_at', { ascending: true })

      let rows = (data || []).map(c => ({ ...c, like_count: c.comment_likes?.[0]?.count || 0, liked_by_me: false }))

      if (user && rows.length > 0) {
        const ids = rows.map(r => r.id)
        const { data: myLikes } = await supabase
          .from('comment_likes').select('comment_id').eq('user_id', user.id).in('comment_id', ids)
        const likedSet = new Set((myLikes || []).map(l => l.comment_id))
        rows = rows.map(r => ({ ...r, liked_by_me: likedSet.has(r.id) }))
      }

      setComments(rows)
      setCommentsLoading(false)
    }
  }

  async function toggleCommentLike(commentId) {
    if (!user) return window.location.assign('/login')
    const target = (comments || []).find(c => c.id === commentId)
    if (!target) return
    if (target.liked_by_me) {
      await supabase.from('comment_likes').delete().eq('comment_id', commentId).eq('user_id', user.id)
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, liked_by_me: false, like_count: Math.max(0, c.like_count - 1) } : c))
    } else {
      await supabase.from('comment_likes').insert({ comment_id: commentId, user_id: user.id })
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, liked_by_me: true, like_count: c.like_count + 1 } : c))
    }
  }

  async function submitComment() {
    if (!user) return window.location.assign('/login')
    const body = newComment.trim()
    if (!body || postingComment) return
    setPostingComment(true)
    const { data, error } = await supabase
      .from('commit_comments')
      .insert({ commit_id: commit.id, user_id: user.id, body })
      .select('*, profiles!commit_comments_user_id_fkey(display_name, full_name, avatar_url, username)')
      .single()
    setPostingComment(false)
    if (error) { alert(error.message); return }
    setComments(prev => [...(prev || []), { ...data, like_count: 0, liked_by_me: false }])
    setCommentCount(c => c + 1)
    setNewComment('')
  }

  async function submitReply(targetComment) {
    if (!user) return window.location.assign('/login')
    const body = replyText.trim()
    if (!body || postingReply) return
    // One level deep: replying to a reply attaches to its original parent instead of nesting further.
    const effectiveParentId = targetComment.parent_comment_id || targetComment.id
    setPostingReply(true)
    const { data, error } = await supabase
      .from('commit_comments')
      .insert({ commit_id: commit.id, user_id: user.id, body, parent_comment_id: effectiveParentId })
      .select('*, profiles!commit_comments_user_id_fkey(display_name, full_name, avatar_url, username)')
      .single()
    setPostingReply(false)
    if (error) { alert(error.message); return }
    setComments(prev => [...(prev || []), { ...data, like_count: 0, liked_by_me: false }])
    setCommentCount(c => c + 1)
    setReplyText('')
    setReplyingTo(null)
  }

  // Group into top-level comments + a map of parent id -> its replies (one level deep).
  const topLevelComments = (comments || []).filter(c => !c.parent_comment_id)
  const repliesByParent = {}
  ;(comments || []).forEach(c => {
    if (c.parent_comment_id) {
      repliesByParent[c.parent_comment_id] = repliesByParent[c.parent_comment_id] || []
      repliesByParent[c.parent_comment_id].push(c)
    }
  })

  return (
    <div className={`commit-card ${commit.is_system ? 'commit-card-system' : ''} ${disputed ? 'commit-card-disputed' : ''}`}>
      <div className="commit-card-avatar">
        {profileData?.avatar_url
          ? <img src={profileData.avatar_url} alt={name} />
          : <span>{name.charAt(0).toUpperCase()}</span>}
      </div>

      <div className="commit-card-body">
        <div className="commit-card-head">
          <Link to={profileData?.username ? `/u/${profileData.username}` : '#'} className="commit-card-name">
            {name}
          </Link>
          <span className="commit-card-dot">·</span>
          <span className="commit-card-time">{timeAgo(commit.created_at)}</span>
          {showStatusBadge && (
            <span className={`commit-card-status commit-card-status-${statusKey}`}>
              {STATUS_LABEL[statusKey]}
            </span>
          )}
        </div>

        {commit.is_removed ? (
          <div className="commit-card-removed-notice">
            <strong>Removed from the wall</strong> by a moderator{commit.removed_reason ? ` — "${commit.removed_reason}"` : ''}.
            {(isAdmin || user?.id === commit.user_id) && (
              <span> Only visible to you{isAdmin ? ' and admins' : ''}.</span>
            )}
            {isAdmin && (
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={restoreCommit} disabled={busy}>
                Restore to wall
              </button>
            )}
          </div>
        ) : (
          <>
            {project && (
              <Link to={`/project/${project.id}`} className="commit-card-project-tag">
                part of "{project.title}"
              </Link>
            )}

            <p className="commit-card-message">{commit.message}</p>

            {commit.media_url && commit.commit_type === 'photo' && (
              <img src={commit.media_url} alt="" className="commit-card-media" />
            )}
            {commit.media_url && commit.commit_type === 'video' && (
              <VideoEmbed url={commit.media_url} />
            )}
            {commit.media_url && commit.commit_type === 'link' && (
              <a href={commit.media_url} target="_blank" rel="noreferrer" className="commit-card-link">
                {commit.media_url}
              </a>
            )}

            {disputed && (
              <div className="commit-card-dispute-note">
                This commit has been flagged by enough independent people to be marked disputed.
                The creator can respond below.
              </div>
            )}
          </>
        )}

        {!commit.is_removed && (!commit.is_system || isAdmin) && (
          <div className="commit-card-actions">
            {!commit.is_system && (
              <>
                <button className={`commit-action ${liked ? 'active' : ''}`} onClick={toggleLike} disabled={busy}>
                  <HeartIcon filled={liked} />
                  <span>{likeCount}</span>
                </button>

                <button className={`commit-action ${showComments ? 'active-comment' : ''}`} onClick={toggleComments}>
                  <CommentIcon />
                  <span>{commentCount}</span>
                </button>

                <button
                  className={`commit-action ${flagged ? 'active-flag' : ''}`}
                  onClick={() => !flagged && setShowFlagBox(v => !v)}
                  disabled={busy || flagged}
                >
                  <FlagIcon />
                  <span>{flagged ? 'Flagged' : 'Flag'}</span>
                </button>
              </>
            )}

            {isAdmin && (
              <button className="commit-action commit-action-admin" onClick={() => setShowRemoveBox(v => !v)} disabled={busy}>
                <TrashIcon />
                <span>Remove</span>
              </button>
            )}
          </div>
        )}

        {showComments && (
          <div className="commit-comments">
            {commentsLoading ? (
              <div style={{ padding: '8px 0' }}><span className="loading-spinner" /></div>
            ) : (
              <>
                {topLevelComments.length === 0 && <p className="commit-comments-empty">No comments yet.</p>}
                {topLevelComments.map(c => {
                  const cName = c.profiles?.display_name || c.profiles?.full_name || 'Anonymous'
                  const replies = repliesByParent[c.id] || []
                  return (
                    <div key={c.id} className="commit-comment-thread">
                      <div className="commit-comment-row">
                        <div className="commit-comment-avatar">
                          {c.profiles?.avatar_url
                            ? <img src={c.profiles.avatar_url} alt={cName} />
                            : <span>{cName.charAt(0).toUpperCase()}</span>}
                        </div>
                        <div className="commit-comment-body">
                          <span className="commit-comment-name">{cName}</span>
                          <span className="commit-comment-text">{c.body}</span>
                          <div className="commit-comment-actions">
                            <button className={`commit-comment-like-btn ${c.liked_by_me ? 'active' : ''}`} onClick={() => toggleCommentLike(c.id)}>
                              <MiniHeartIcon filled={c.liked_by_me} />
                              <span>{c.like_count || 0}</span>
                            </button>
                            {user && (
                              <button className="commit-comment-reply-btn" onClick={() => setReplyingTo(replyingTo === c.id ? null : c.id)}>
                                Reply
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {replies.map(r => {
                        const rName = r.profiles?.display_name || r.profiles?.full_name || 'Anonymous'
                        return (
                          <div key={r.id} className="commit-comment-row commit-comment-reply-row">
                            <div className="commit-comment-avatar commit-comment-avatar-sm">
                              {r.profiles?.avatar_url
                                ? <img src={r.profiles.avatar_url} alt={rName} />
                                : <span>{rName.charAt(0).toUpperCase()}</span>}
                            </div>
                            <div className="commit-comment-body">
                              <span className="commit-comment-name">{rName}</span>
                              <span className="commit-comment-text">{r.body}</span>
                              <div className="commit-comment-actions">
                                <button className={`commit-comment-like-btn ${r.liked_by_me ? 'active' : ''}`} onClick={() => toggleCommentLike(r.id)}>
                                  <MiniHeartIcon filled={r.liked_by_me} />
                                  <span>{r.like_count || 0}</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      })}

                      {replyingTo === c.id && (
                        <div className="commit-comment-input commit-comment-reply-input">
                          <textarea
                            placeholder={`Reply to ${cName}…`}
                            value={replyText}
                            onChange={e => setReplyText(e.target.value)}
                            rows={1}
                            maxLength={500}
                            autoFocus
                          />
                          <button className="btn btn-primary btn-sm" onClick={() => submitReply(c)} disabled={postingReply || !replyText.trim()}>
                            Post
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
                {user ? (
                  <div className="commit-comment-input">
                    <textarea
                      placeholder="Add a comment…"
                      value={newComment}
                      onChange={e => setNewComment(e.target.value)}
                      rows={1}
                      maxLength={500}
                    />
                    <button className="btn btn-primary btn-sm" onClick={submitComment} disabled={postingComment || !newComment.trim()}>
                      Post
                    </button>
                  </div>
                ) : (
                  <p className="commit-comments-empty"><Link to="/login">Sign in</Link> to comment.</p>
                )}
              </>
            )}
          </div>
        )}

        {showFlagBox && (
          <div className="commit-flag-box">
            <textarea
              placeholder="Optional: why does this look suspicious?"
              value={flagReason}
              onChange={e => setFlagReason(e.target.value)}
              rows={2}
            />
            <div className="commit-flag-box-actions">
              <button className="btn btn-secondary" onClick={() => setShowFlagBox(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitFlag} disabled={busy}>Submit flag</button>
            </div>
          </div>
        )}

        {showRemoveBox && (
          <div className="commit-flag-box">
            <textarea
              placeholder="Reason for removal (shown to the creator)"
              value={removeReason}
              onChange={e => setRemoveReason(e.target.value)}
              rows={2}
            />
            <div className="commit-flag-box-actions">
              <button className="btn btn-secondary" onClick={() => setShowRemoveBox(false)}>Cancel</button>
              <button className="btn verdict-suspicious" onClick={submitRemove} disabled={busy}>Remove from wall</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function HeartIcon({ filled }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8">
      <path d="M12 21s-7.5-4.7-10-9.1C.4 8.4 2 4.5 5.6 4c2-.3 3.7.7 6.4 3.3C14.7 4.7 16.4 3.7 18.4 4c3.6.5 5.2 4.4 3.6 7.9C19.5 16.3 12 21 12 21z" />
    </svg>
  )
}

function MiniHeartIcon({ filled }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
      <path d="M12 21s-7.5-4.7-10-9.1C.4 8.4 2 4.5 5.6 4c2-.3 3.7.7 6.4 3.3C14.7 4.7 16.4 3.7 18.4 4c3.6.5 5.2 4.4 3.6 7.9C19.5 16.3 12 21 12 21z" />
    </svg>
  )
}

function CommentIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function FlagIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 3v18" strokeLinecap="round" />
      <path d="M5 4h11l-2.5 4L16 12H5" strokeLinejoin="round" />
    </svg>
  )
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
